import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  RefreshCw, Plus, CheckCircle2, ClipboardCheck, X,
  ChevronUp, ChevronDown, ChevronsUpDown, Loader2,
  Upload,
} from 'lucide-react';
import Breadcrumb, {
  BREADCRUMB_ACTION_BTN,
} from '../../../components/Breadcrumb/Breadcrumb';
import SearchableSelect from '../../../components/common/SearchableSelect';
import { usePermission } from '../../../config/permissions';
import {
  fetchTaxRulesByYear,
  createTaxRule,
  clearTaxRules,
  approveTaxTable,
} from '../../../store/slices/PayrollTaxTableSlice';
import ImportTaxTableModal from '../../../components/PayRoll/ImportTaxTableModal';
import useToast from '../../../utils/useToast';
import Toast from '../../../components/Toast/Toast';
import { useConfirm } from '../../../utils/ConfirmContext';


const FIELD =
  'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 text-gray-700';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

/* Sticky offsets — match the rest of the app (see OTDetails). */
const STICKY_TOP = -24;
const STICKY_BOTTOM = -12;

/* ------------------------------------------------------------------ */
/* Constants / helpers                                                 */
/* ------------------------------------------------------------------ */
function buildYearOptions() {
  const current = 2026;
  const years = [];
  for (let y = current; y >= current - 10; y--) {
    years.push({ id: String(y), displayName: String(y) });
  }
  return years;
}
const YEAR_OPTIONS = buildYearOptions();

const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };
const stickyStyle = (col) =>
  col.sticky ? { position: 'sticky', left: col.left } : undefined;

/* ------------------------------------------------------------------ */
/* Columns — Year + Table No are sticky for horizontal scroll         */
/* ------------------------------------------------------------------ */
const COLUMNS = [
  {
    key: 'HTT_YEAR',
    label: 'Year',
    align: 'center',
    width: 120,
    sticky: true,
    left: 0,
    sortable: true,
  },
  {
    key: 'HTT_TABLE_NO',
    label: 'Table No',
    align: 'center',
    width: 140,
    sticky: true,
    left: 70,
    sortable: true,
  },
  { key: 'HTT_RULE_NO', label: 'Rule No', align: 'center', width: 160, sortable: true },
  { key: 'HTT_INCOME_RANGE', label: 'Income Range', align: 'left', width: 150, sortable: false },
  { key: 'HTT_MIN_AMOUNT', label: 'Min Amount', align: 'right', width: 180, sortable: true },
  { key: 'HTT_MAX_AMOUNT', label: 'Max Amount', align: 'right', width: 180, sortable: true },
  { key: 'HTT_TAX_PERCENTAGE', label: 'Tax %', align: 'right', width: 160, sortable: true },
  { key: 'HTT_DEDUC_AMOUNT', label: 'Deduction Amount', align: 'right', width: 200, sortable: true },
  // { key: '__approval_action', label: 'Approval', align: 'center', width: 240, sortable: false },
];

const TABLE_WIDTH = COLUMNS.reduce((total, col) => total + col.width, 0);

/* ------------------------------------------------------------------ */
/* Approval Action Cell — permission-driven                           */
/* ------------------------------------------------------------------ */
// function ApprovalAction({ row, onPrepare, onApprove }) {
//   const canApprove = usePermission('tax:approve');
//   const canPrepare = usePermission('tax:prepare');
//   const isApproved = String(row.HTT_IS_APPROVED) === 'True';

//   if (canApprove) {
//     return (
//       <div className="flex justify-center">
//         <button
//           type="button"
//           onClick={() => !isApproved && onApprove(row)}
//           disabled={isApproved}
//           title={isApproved ? 'Already approved' : 'Approve this rule'}
//           className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1 text-xs font-medium transition-colors ${
//             isApproved
//               ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
//               : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
//           }`}
//         >
//           <CheckCircle2 size={13} />
//           {isApproved ? 'Approved' : 'Approve'}
//         </button>
//       </div>
//     );
//   }

//   if (canPrepare) {
//     return (
//       <div className="flex justify-center">   {/* was justify-end */}
//         <button
//           type="button"
//           onClick={() => onPrepare(row)}
//           title="Mark preparation complete"
//           className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
//         >
//           <ClipboardCheck size={13} />
//           Preparation Complete
//         </button>
//       </div>
//     );
//   }

//   return <span className="text-gray-400">—</span>;
// }

/* ------------------------------------------------------------------ */
/* FormField wrapper                                                   */
/* ------------------------------------------------------------------ */
function FormField({ label, required, error, hint, children }) {
  return (
    <div>
      <label className={LABEL}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Add Rule Modal (unchanged behaviour)                               */
/* ------------------------------------------------------------------ */
function AddRuleModal({
  open,
  onClose,
  onSubmit,
  year,
  adding,
  existingRules = [],
}) {
  const initialForm = {
    HTT_YEAR: '',
    HTT_MIN_AMOUNT: '',
    HTT_MAX_AMOUNT: '',
    HTT_TAX_PERCENTAGE: '',
    HTT_DEDUC_AMOUNT: '',
    HTT_INCOME_RANGE: '',
  };

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [mode, setMode] = useState('existing');
  const [touched, setTouched] = useState({
    incomeRange: false,
    min: false,
    deduc: false,
  });
  const modalRef = useRef(null);

  const { activeTableNo, isActiveTableApproved } = useMemo(() => {
    if (!Array.isArray(existingRules)) return { activeTableNo: null, isActiveTableApproved: false };
    const activeRow = existingRules.find(
      (r) => String(r.HTT_STATUS).toUpperCase() === 'A'
    );
    return {
      activeTableNo: activeRow ? String(activeRow.HTT_TABLE_NO) : null,
      isActiveTableApproved: activeRow ? String(activeRow.HTT_IS_APPROVED) === 'True' : false,
    }
  }, [existingRules]);

  const hasActiveTable = Boolean(activeTableNo);

  useEffect(() => {
    if (open) {
      setForm({ ...initialForm, HTT_YEAR: year || '' });
      setErrors({});
      // Force 'new' if there is no active table or if it is already approved
      setMode(hasActiveTable && !isActiveTableApproved ? 'existing' : 'new');
      setTouched({ incomeRange: false, min: false, deduc: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, year, hasActiveTable, isActiveTableApproved]);

  const relevantRows = useMemo(() => {
    if (mode !== 'existing' || !Array.isArray(existingRules) || !activeTableNo)
      return [];
    return existingRules.filter(
      (r) => String(r.HTT_TABLE_NO) === String(activeTableNo)
    );
  }, [existingRules, activeTableNo, mode]);

  const previousBracket = useMemo(() => {
    if (relevantRows.length === 0) return null;
    return [...relevantRows].sort(
      (a, b) => Number(b.HTT_MAX_AMOUNT) - Number(a.HTT_MAX_AMOUNT)
    )[0];
  }, [relevantRows]); 

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || touched.incomeRange) return;
    const min = form.HTT_MIN_AMOUNT;
    const max = form.HTT_MAX_AMOUNT;
    if (min !== '' && max !== '') {
      const minNum = Number(min);
      const maxNum = Number(max);
      if (!isNaN(minNum) && !isNaN(maxNum)) {
        setForm((prev) => ({
          ...prev,
          HTT_INCOME_RANGE: `${minNum.toLocaleString()} - ${maxNum.toLocaleString()}`,
        }));
      }
    }
  }, [form.HTT_MIN_AMOUNT, form.HTT_MAX_AMOUNT, open, touched.incomeRange]);

  useEffect(() => {
    if (!open || touched.min || !previousBracket) return;
    const prevMax = Number(previousBracket.HTT_MAX_AMOUNT);
    if (!isNaN(prevMax)) {
      setForm((prev) => ({ ...prev, HTT_MIN_AMOUNT: String(prevMax + 1) }));
    }
  }, [previousBracket, open, touched.min]);

  useEffect(() => {
    if (!open || touched.deduc) return;
    if (form.HTT_TAX_PERCENTAGE === '') return;
    const rate = Number(form.HTT_TAX_PERCENTAGE);
    if (isNaN(rate)) return;

    if (!previousBracket) {
      setForm((prev) => ({ ...prev, HTT_DEDUC_AMOUNT: '0.00' }));
      return;
    }

    const prevMax = Number(previousBracket.HTT_MAX_AMOUNT);
    const prevRate = Number(previousBracket.HTT_TAX_PERCENTAGE);
    const prevDeduc = Number(previousBracket.HTT_DEDUC_AMOUNT);
    if ([prevMax, prevRate, prevDeduc].some((v) => isNaN(v))) return;

    const suggested = prevDeduc + prevMax * ((rate - prevRate) / 100);
    setForm((prev) => ({ ...prev, HTT_DEDUC_AMOUNT: suggested.toFixed(2) }));
  }, [previousBracket, form.HTT_TAX_PERCENTAGE, open, touched.deduc]);

  if (!open) return null;

  const bracketTaxPreview = (() => {
    if (
      form.HTT_MIN_AMOUNT === '' ||
      form.HTT_MAX_AMOUNT === '' ||
      form.HTT_TAX_PERCENTAGE === ''
    )
      return null;
    const min = Number(form.HTT_MIN_AMOUNT);
    const max = Number(form.HTT_MAX_AMOUNT);
    const rate = Number(form.HTT_TAX_PERCENTAGE);
    if ([min, max, rate].some((v) => isNaN(v)) || max < min) return null;
    return (max - min + 1) * (rate / 100);
  })();

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleManualChange = (field, value, touchKey) => {
    handleChange(field, value);
    if (touchKey) {
      setTouched((prev) => ({ ...prev, [touchKey]: true }));
    }
  };

  const validate = () => {
    const errs = {};

    if (!String(form.HTT_YEAR).trim()) {
      errs.HTT_YEAR = 'Year is required';
    } else {
      const y = Number(form.HTT_YEAR);
      if (isNaN(y) || y < 2000 || y > 2100) {
        errs.HTT_YEAR = 'Enter a valid year (2000–2100)';
      }
    }

    if (String(form.HTT_MIN_AMOUNT).trim() === '')
      errs.HTT_MIN_AMOUNT = 'Min Amount is required';
    if (String(form.HTT_MAX_AMOUNT).trim() === '')
      errs.HTT_MAX_AMOUNT = 'Max Amount is required';
    if (
      form.HTT_MIN_AMOUNT !== '' &&
      form.HTT_MAX_AMOUNT !== '' &&
      Number(form.HTT_MAX_AMOUNT) < Number(form.HTT_MIN_AMOUNT)
    ) {
      errs.HTT_MAX_AMOUNT = 'Max must be greater than Min';
    }
    if (String(form.HTT_TAX_PERCENTAGE).trim() === '')
      errs.HTT_TAX_PERCENTAGE = 'Tax % is required';
    if (
      form.HTT_TAX_PERCENTAGE !== '' &&
      (Number(form.HTT_TAX_PERCENTAGE) < 0 ||
        Number(form.HTT_TAX_PERCENTAGE) > 100)
    ) {
      errs.HTT_TAX_PERCENTAGE = 'Tax % must be between 0 and 100';
    }
    if (String(form.HTT_DEDUC_AMOUNT).trim() === '')
      errs.HTT_DEDUC_AMOUNT = 'Deduction Amount is required';

    if (mode === 'existing' && !activeTableNo) {
      errs.HTT_TABLE_NO =
        'No active table found for this year — start a new table instead';
    }

    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    onSubmit({
      year: Number(form.HTT_YEAR),
      minAmount: Number(form.HTT_MIN_AMOUNT),
      maxAmount: Number(form.HTT_MAX_AMOUNT),
      taxPercentage: Number(form.HTT_TAX_PERCENTAGE),
      deducAmount: Number(form.HTT_DEDUC_AMOUNT),
      incomeRange: form.HTT_INCOME_RANGE,
      tableNo: mode === 'existing' ? Number(activeTableNo) : undefined,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Add Tax Rule</h2>
            <p className="mt-0.5 text-sm text-gray-500">
              Fill in the income bracket and tax details.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="max-h-[65vh] space-y-5 overflow-y-auto px-6 py-5">
            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Identification
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField label="Year" required error={errors.HTT_YEAR}>
                  <input
                    type="number"
                    value={form.HTT_YEAR}
                    onChange={(e) => handleChange('HTT_YEAR', e.target.value)}
                    placeholder="e.g. 2024"
                    className={FIELD}
                  />
                </FormField>

                <FormField
                  label="Table No"
                  error={errors.HTT_TABLE_NO}
                  hint={
                    mode === 'existing'
                      ? 'Auto-fetched — currently active table'
                      : 'Auto-assigned'
                  }
                >
                  <div className={`${FIELD} flex items-center bg-gray-50 text-gray-500`}>
                    {mode === 'existing' ? activeTableNo : 'Auto'}
                  </div>
                </FormField>

                <FormField label="Rule No" hint="Auto-assigned">
                  <div className={`${FIELD} flex items-center bg-gray-50 text-gray-500`}>
                    Auto
                  </div>
                </FormField>
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Add To
              </h3>
              {hasActiveTable ? (
                <>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setMode('existing')}
                      disabled={isActiveTableApproved}
                      className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        isActiveTableApproved
                          ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                          : mode === 'existing'
                          ? 'border-primary-300 bg-primary-50 text-primary-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Current active table (No. {activeTableNo})
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode('new')}
                      className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        mode === 'new'
                          ? 'border-primary-300 bg-primary-50 text-primary-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Start a new table
                    </button>
                  </div>
                  {mode === 'new' && (
                    <p className="mt-2 text-xs text-amber-600">
                      This will deactivate the current table (No. {activeTableNo}) for{' '}
                      {form.HTT_YEAR || 'this year'} once saved.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  No active table yet for {form.HTT_YEAR || 'this year'} — this will be
                  the first table, and its number will be auto-assigned.
                </p>
              )}
            </section>

            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Income Bracket
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  label="Min Amount"
                  required
                  error={errors.HTT_MIN_AMOUNT}
                  hint={
                    previousBracket
                      ? `Suggested from previous bracket's Max + 1`
                      : undefined
                  }
                >
                  <input
                    type="number"
                    value={form.HTT_MIN_AMOUNT}
                    onChange={(e) =>
                      handleManualChange('HTT_MIN_AMOUNT', e.target.value, 'min')
                    }
                    placeholder="e.g. 0"
                    className={FIELD}
                  />
                </FormField>

                <FormField label="Max Amount" required error={errors.HTT_MAX_AMOUNT}>
                  <input
                    type="number"
                    value={form.HTT_MAX_AMOUNT}
                    onChange={(e) => handleChange('HTT_MAX_AMOUNT', e.target.value)}
                    placeholder="e.g. 100000"
                    className={FIELD}
                  />
                </FormField>

                <div className="sm:col-span-2">
                  <FormField label="Income Range" hint="Suggested from Min & Max Amount">
                    <input
                      type="text"
                      value={form.HTT_INCOME_RANGE}
                      onChange={(e) =>
                        handleManualChange('HTT_INCOME_RANGE', e.target.value, 'incomeRange')
                      }
                      placeholder="e.g. 0 - 100,000"
                      className={FIELD}
                    />
                  </FormField>
                </div>
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Tax Computation
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label="Tax %" required error={errors.HTT_TAX_PERCENTAGE}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={form.HTT_TAX_PERCENTAGE}
                    onChange={(e) => handleChange('HTT_TAX_PERCENTAGE', e.target.value)}
                    placeholder="e.g. 6"
                    className={FIELD}
                  />
                </FormField>

                <FormField
                  label="Deduction Amount"
                  required
                  error={errors.HTT_DEDUC_AMOUNT}
                  hint={
                    previousBracket
                      ? 'Auto-suggested from the previous bracket'
                      : 'First bracket in this table — defaults to 0'
                  }
                >
                  <input
                    type="number"
                    step="0.01"
                    value={form.HTT_DEDUC_AMOUNT}
                    onChange={(e) =>
                      handleManualChange('HTT_DEDUC_AMOUNT', e.target.value, 'deduc')
                    }
                    placeholder="e.g. 6000"
                    className={FIELD}
                  />
                </FormField>

                {bracketTaxPreview !== null && (
                  <div className="sm:col-span-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                    Tax within this bracket ≈ (Max − Min + 1) × Rate ={' '}
                    <span className="font-medium text-gray-700">
                      {bracketTaxPreview.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={adding}
              className={`inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 ${
                adding ? 'cursor-not-allowed opacity-70' : ''
              }`}
            >
              {adding ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Plus size={14} />
                  Save Rule
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Component                                                      */
/* ------------------------------------------------------------------ */
function TaxTable() {
  const dispatch = useDispatch();
  const {
    rows: taxRules,
    loading,
    adding,
    error,
  } = useSelector((s) => s.payrollTaxTable);

  const canCreate = usePermission('tax:create');
  const canApprove = usePermission('tax:approve');

  const confirm = useConfirm();

  const [search, setSearch] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedTableNo, setSelectedTableNo] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [sort, setSort] = useState({ key: 'HTT_RULE_NO', dir: 'asc' });

  const { toast, showToast, hideToast } = useToast();

  /* Default year */
  useEffect(() => {
    if (!selectedYear && YEAR_OPTIONS.length > 0) {
      setSelectedYear(YEAR_OPTIONS[0].id);
    }
  }, [selectedYear]);

  /* Fetch on year change */
  useEffect(() => {
    if (!selectedYear) return;
    dispatch(clearTaxRules());
    setSearch('');
    setSelectedTableNo('');
    dispatch(fetchTaxRulesByYear(selectedYear));
  }, [selectedYear, dispatch]);

  /* Table No options */
  const tableNoOptions = useMemo(() => {
    if (!Array.isArray(taxRules)) return [];
    const unique = Array.from(
      new Set(
        taxRules
          .map((r) => r.HTT_TABLE_NO)
          .filter((v) => v !== undefined && v !== null && v !== '')
      )
    );
    return unique
      .sort((a, b) => Number(a) - Number(b))
      .map((v) => ({ id: String(v), displayName: String(v) }));
  }, [taxRules]);

  /* Filter */
  const filtered = useMemo(() => {
    if (!Array.isArray(taxRules)) return [];
    let result = taxRules;

    if (selectedTableNo) {
      result = result.filter(
        (row) => String(row.HTT_TABLE_NO) === String(selectedTableNo)
      );
    }

    const q = search.trim().toLowerCase();
    if (!q) return result;

    return result.filter((row) => {
      return (
        String(row.HTT_YEAR || '').toLowerCase().includes(q) ||
        String(row.HTT_TABLE_NO || '').toLowerCase().includes(q) ||
        String(row.HTT_RULE_NO || '').toLowerCase().includes(q) ||
        String(row.HTT_INCOME_RANGE || '').toLowerCase().includes(q) ||
        String(row.HTT_STATUS || '').toLowerCase().includes(q) ||
        String(row.CREATED_BY || '').toLowerCase().includes(q) ||
        String(row.HTT_APPROVED_BY || '').toLowerCase().includes(q)
      );
    });
  }, [taxRules, search, selectedTableNo]);

  /* Sort */ 
  const sorted = useMemo(() => {
    const list = [...filtered];
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;

    list.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      const an = Number(av);
      const bn = Number(bv);

      if (av !== '' && bv !== '' && !isNaN(an) && !isNaN(bn)) {
        return (an - bn) * mult;
      }
      return String(av ?? '').localeCompare(String(bv ?? '')) * mult;
    });
    return list;
  }, [filtered, sort]);

  const toggleSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );

  const formatCurrency = (value) => {
    const num = Number(value);
    if (isNaN(num)) return '—';
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // const handlePrepare = (row) => {
  //   console.log('Preparation Complete for rule:', row.HTT_RULE_NO);
  // };

  // const handleApprove = (row) => {
  //   if (!canApprove) {
  //     console.warn('Approve blocked: missing tax:approve permission');
  //     return;
  //   }
  //   console.log('Approve rule:', row.HTT_RULE_NO);
  // };

  const displayedTableNos = Array.from(new Set(sorted.map(r => r.HTT_TABLE_NO).filter(Boolean)));
  const singleTableDisplayed = displayedTableNos.length === 1;
  const currentTableToApprove = singleTableDisplayed ? displayedTableNos[0] : null;

  // A table is considered approved if ALL of its rules are approved
  const isTableApproved = singleTableDisplayed && sorted.every(r => String(r.HTT_IS_APPROVED) === 'True');

  const handleApproveTable = async () => {
    if (!canApprove || !currentTableToApprove || !selectedYear) return;

    const confirmed = await confirm({
      title: "Approve Tax Table?",
      message: `Are you sure you want to approve Table No. ${currentTableToApprove} for ${selectedYear}?`,
      confirmLabel: "Approve",
      cancelLabel: "Cancel",
    });

    if (!confirmed) return;

    const result = await dispatch(approveTaxTable({ year: selectedYear, tableNo: currentTableToApprove }));
    if (approveTaxTable.fulfilled.match(result)) {
      showToast(`Tax Table No. ${currentTableToApprove} approved successfully.`, 'success');
    } else {
      showToast(result.payload || "Failed to approve tax table.", 'error');
    }
  }

  const handleAddRule = async (payload) => {
    if (!canCreate) return;
    const result = await dispatch(createTaxRule(payload));
    if (createTaxRule.fulfilled.match(result)) {
      setIsModalOpen(false);
    }
  };

  const handleRefresh = () => {
    if (selectedYear) {
      dispatch(fetchTaxRulesByYear(selectedYear));
    }
  };

  /* ───────── Scroll plumbing (mirrors OTDetails) ───────── */
  const tableScrollRef = useRef(null);
  const barRef = useRef(null);
  const headerRef = useRef(null);
  const syncing = useRef(false);
  const [bar, setBar] = useState({ scrollWidth: 0, hasOverflow: false });

  const measureBar = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const next = {
      scrollWidth: el.scrollWidth,
      hasOverflow: el.scrollWidth > el.clientWidth + 1,
    };
    setBar((prev) =>
      prev.scrollWidth === next.scrollWidth && prev.hasOverflow === next.hasOverflow
        ? prev
        : next
    );
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    measureBar();
    const ro = new ResizeObserver(measureBar);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    window.addEventListener('resize', measureBar);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureBar);
    };
  }, [measureBar, sorted]);

  const onTableScroll = () => {
    if (headerRef.current && tableScrollRef.current) {
      headerRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
    if (syncing.current) {
      syncing.current = false;
      return;
    }
    if (barRef.current && tableScrollRef.current) {
      syncing.current = true;
      barRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
  };

  const onBarScroll = () => {
    if (syncing.current) {
      syncing.current = false;
      return;
    }
    if (barRef.current && tableScrollRef.current) {
      syncing.current = true;
      tableScrollRef.current.scrollLeft = barRef.current.scrollLeft;
    }
  };

  /* Per-column cell renderer */
  const renderCell = (col, r) => {
    switch (col.key) {
      case 'HTT_YEAR':
        return (
          <span className="font-semibold text-gray-900 tabular-nums">
            {r.HTT_YEAR || '—'}
          </span>
        );
      case 'HTT_TABLE_NO':
        return (
          <span className="font-medium text-gray-900 tabular-nums">
            {r.HTT_TABLE_NO || '—'}
          </span>
        );
      case 'HTT_RULE_NO':
        return (
          <span className="font-medium text-gray-700 tabular-nums">
            {r.HTT_RULE_NO || '—'}
          </span>
        );
      case 'HTT_INCOME_RANGE':
        return <span className="text-gray-600">{r.HTT_INCOME_RANGE || '—'}</span>;
      case 'HTT_MIN_AMOUNT':
        return (
          <span className="text-gray-600 tabular-nums">
            {formatCurrency(r.HTT_MIN_AMOUNT)}
          </span>
        );
      case 'HTT_MAX_AMOUNT':
        return (
          <span className="text-gray-600 tabular-nums">
            {formatCurrency(r.HTT_MAX_AMOUNT)}
          </span>
        );
      case 'HTT_TAX_PERCENTAGE':
        return (
          <span className="text-gray-700 font-medium tabular-nums">
            {r.HTT_TAX_PERCENTAGE != null
              ? `${Number(r.HTT_TAX_PERCENTAGE).toFixed(2)}%`
              : '—'}
          </span>
        );
      case 'HTT_DEDUC_AMOUNT':
        return (
          <span className="text-gray-600 tabular-nums">
            {formatCurrency(r.HTT_DEDUC_AMOUNT)}
          </span>
        );
      // case '__approval_action':
      //   return (
      //     <ApprovalAction
      //       row={r}
      //       onPrepare={handlePrepare}
      //       onApprove={handleApprove}
      //     />
      //   );
      default:
        return r[col.key] ?? '—';
    }
  };

  const rowKeyOf = (row) =>
    `${row.HTT_YEAR}-${row.HTT_TABLE_NO}-${row.HTT_RULE_NO}`;

  return (
    <div className="p-6 space-y-4">
      <Breadcrumb
        trail={['Payroll', 'Tax Table']}
        title="Tax Table"
        subtitle={`View tax rules${selectedYear ? ` for year ${selectedYear}` : ''}.`}
        action={
          <div className="flex items-center gap-2">
            {canCreate && (
              <button
                onClick={() => setIsModalOpen(true)}
                className={BREADCRUMB_ACTION_BTN}
              >
                <Plus size={15} />
                Add Rule
              </button>
            )}

            <button onClick={handleRefresh} className={BREADCRUMB_ACTION_BTN}>
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>

            {canCreate && (
              <button 
                type='button'
                onClick={() => setIsImportModalOpen(true)}
                className="flex items-center justify-center gap-2 h-9 px-4 rounded-md bg-white border border-gray-300 text-[13px] font-medium text-gray-700 hover:bg-gray-50 focus:ring-1 focus:ring-primary-500 transition-colors shadow-sm"
              >
                <Upload size={14} className="text-gray-500" />
                Import
              </button>
            )}
          </div>
        }
      />

      {/* Filters — same visual pattern as OTDetails */}
      <div className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 shadow-sm space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <span className={LABEL}>Year</span>
            <SearchableSelect
              value={selectedYear}
              onChange={setSelectedYear}
              options={YEAR_OPTIONS.map((o) => ({ value: o.id, label: o.displayName }))}
              placeholder="Select Year"
              searchPlaceholder="Search year..."
              className={FIELD}
            />
          </div>

          <div>
            <span className={LABEL}>Table No</span>
            <SearchableSelect
              value={selectedTableNo}
              onChange={setSelectedTableNo}
              options={tableNoOptions.map((o) => ({
                value: o.id,
                label: o.displayName,
              }))}
              emptyOption={{ value: '', label: 'All Tables' }}
              placeholder="All Tables"
              searchPlaceholder="Search table no..."
              disabled={tableNoOptions.length === 0}
              className={`${FIELD} ${
                tableNoOptions.length === 0
                  ? 'cursor-not-allowed bg-gray-50 opacity-70'
                  : ''
              }`}
            />
          </div>

          <div className="md:col-span-2">
            <span className={LABEL}>Search</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by year, table no, rule no, income range..."
              className={FIELD}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Table card */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {/* Title bar */}
        <div className="flex flex-col gap-3 border-b border-gray-100 p-5 lg:flex-row lg:items-center lg:justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Tax Rules</h3>
          <p className="text-xs text-gray-500">
            {sorted.length} of {taxRules?.length || 0} rule
            {taxRules?.length === 1 ? '' : 's'}
            {selectedYear ? ` for ${selectedYear}` : ''}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {/* ── Sticky header ── */}
            <div
              ref={headerRef}
              className="sticky z-30 overflow-hidden bg-gray-50"
              style={{ top: STICKY_TOP }}
            >
              <table
                className="border-separate border-spacing-0 text-sm"
                style={{ minWidth: TABLE_WIDTH, tableLayout: 'fixed' }}
              >
                <colgroup>
                  {COLUMNS.map((c) => (
                    <col key={c.key} style={{ width: c.width }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {COLUMNS.map((c) => {
                      const active = sort.key === c.key;
                      const SortIcon = !active
                        ? ChevronsUpDown
                        : sort.dir === 'asc'
                          ? ChevronUp
                          : ChevronDown;
                      return (
                        <th
                          key={c.key}
                          style={stickyStyle(c)}
                          className={`${c.sticky ? 'sticky z-20' : ''} bg-gray-50 border-b border-gray-200 px-3 py-2.5
                                      text-[12px] font-semibold tracking-wide text-gray-500 select-none
                                      ${ALIGN[c.align]}`}
                          aria-sort={
                            active
                              ? sort.dir === 'asc'
                                ? 'ascending'
                                : 'descending'
                              : 'none'
                          }
                        >
                          <span
                            className={`inline-flex items-center gap-1 ${
                              c.align === 'right' ? 'justify-end w-full' : ''
                            }`}
                          >
                            {c.sortable ? (
                              <button
                                type="button"
                                onClick={() => toggleSort(c.key)}
                                className="hover:text-gray-900"
                              >
                                {c.label}
                              </button>
                            ) : (
                              <span>{c.label}</span>
                            )}
                            {c.sortable && <SortIcon size={12} />}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
              </table>
            </div>

            {/* ── Scrollable body ── */}
            <div
              ref={tableScrollRef}
              onScroll={onTableScroll}
              className="relative overflow-x-auto [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <table
                className="border-separate border-spacing-0 text-sm"
                style={{ minWidth: TABLE_WIDTH, tableLayout: 'fixed' }}
              >
                <colgroup>
                  {COLUMNS.map((c) => (
                    <col key={c.key} style={{ width: c.width }} />
                  ))}
                </colgroup>
                <tbody>
                  {sorted.length === 0 && (
                    <tr>
                      <td
                        colSpan={COLUMNS.length}
                        className="px-5 py-10 text-center text-sm text-gray-400"
                      >
                        {!taxRules || taxRules.length === 0
                          ? 'No tax rules available for this year. Add one to get started.'
                          : 'No results match your search criteria.'}
                      </td>
                    </tr>
                  )}

                  {sorted.map((r) => (
                    <tr key={rowKeyOf(r)} className="group">
                      {COLUMNS.map((c) => (
                        <td
                          key={c.key}
                          style={stickyStyle(c)}
                          className={`${c.sticky ? 'sticky z-10' : ''} bg-white group-hover:bg-gray-50
                                      border-b border-gray-100 px-3 py-2 overflow-hidden
                                      ${ALIGN[c.align]}`}
                        >
                          {renderCell(c, r)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer summary */}
            {sorted.length > 0 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
                <span className="text-xs text-gray-500">
                  Showing{' '}
                  <strong className="text-gray-800">{sorted.length}</strong>{' '}
                  {sorted.length === 1 ? 'entry' : 'entries'}
                </span>

                {canApprove && singleTableDisplayed && (
                  <button
                    type="button"
                    onClick={handleApproveTable}
                    disabled={isTableApproved || loading}
                    className={`inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      isTableApproved 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50'
                    }`}
                  >
                    {loading ? 'Processing...' : isTableApproved ? 'Table Approved' : `Approve Table ${currentTableToApprove}`}
                  </button>
                )}
              </div>
            )}

            {/* ── Sticky horizontal scrollbar ── */}
            <div
              ref={barRef}
              onScroll={onBarScroll}
              className="sticky z-30 overflow-x-auto overflow-y-hidden bg-white/95 border-t border-gray-200 rounded-b-xl"
              style={{
                bottom: STICKY_BOTTOM,
                height: 16,
                display: bar.hasOverflow && sorted.length > 0 ? 'block' : 'none',
              }}
              aria-hidden="true"
            >
              <div style={{ width: bar.scrollWidth, height: 1 }} />
            </div>
          </>
        )}
      </div>

      <AddRuleModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddRule}
        year={selectedYear}
        adding={adding}
        existingRules={taxRules}
      />

      <ImportTaxTableModal 
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        existingRules={taxRules}
        year={selectedYear}
        onSuccess={(count) => {
          showToast(`Successfully imported ${count} tax rules.`, "success");
        }}
      /> 

      <Toast message={toast?.message} type={toast?.type} onClose={hideToast} />
    </div>
  );
}

export default TaxTable;